import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Fade,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import { useTeacher } from '../../context/TeacherContext';

const SUBSCRIPTION_BACKEND_URL = process.env.REACT_APP_SUBSCRIPTION_URL || 'http://localhost:5002';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { teacher } = useTeacher();
  
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);
  const confirmationInProgress = useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const confirmSubscription = async () => {
      // Prevent double execution
      if (confirmationInProgress.current) return;
      
      if (!sessionId) {
        setStatus('error');
        setErrorMessage('No session ID found. Please contact support.');
        return;
      }

      if (!teacher?._id) {
        setStatus('error');
        setErrorMessage('Please log in to complete your subscription.');
        return;
      }

      try {
        confirmationInProgress.current = true;
        setActiveStep(0);

        const token = localStorage.getItem('token');
        
        setActiveStep(1);
        const response = await fetch(`${SUBSCRIPTION_BACKEND_URL}/subscription/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sessionId,
            teacherId: teacher._id
          }),
        });

        setActiveStep(2);
        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setActiveStep(3);
        } else {
          setStatus('error');
          setErrorMessage(data.error || 'Failed to confirm subscription');
        }
      } catch (error) {
        console.error('Error confirming subscription:', error);
        setStatus('error');
        setErrorMessage('Network error. Please contact support if your subscription was charged.');
      }
    };

    confirmSubscription();
  }, [sessionId, teacher?._id]);

  const steps = ['Verifying payment', 'Activating subscription', 'Setting up your account', 'Complete'];

  return (
    <Box sx={{ 
      minHeight: '80vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      p: 3,
      bgcolor: 'background.default'
    }}>
      <Fade in>
        <Card sx={{ maxWidth: 600, width: '100%', borderRadius: 4, overflow: 'visible' }}>
          {/* Header */}
          <Box sx={{ 
            bgcolor: status === 'success' ? 'success.main' : status === 'error' ? 'error.main' : 'primary.main',
            color: 'primary.contrastText',
            p: 4,
            textAlign: 'center',
            borderRadius: '16px 16px 0 0',
            position: 'relative'
          }}>
            {status === 'processing' && (
              <CircularProgress size={48} sx={{ color: 'white', mb: 2 }} />
            )}
            {status === 'success' && (
              <CheckCircleIcon sx={{ fontSize: 64, mb: 2 }} />
            )}
            {status === 'error' && (
              <ErrorIcon sx={{ fontSize: 64, mb: 2 }} />
            )}
            
            <Typography variant="h4" fontWeight="bold">
              {status === 'processing' && 'Processing Payment...'}
              {status === 'success' && 'Welcome to Premium!'}
              {status === 'error' && 'Something Went Wrong'}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {status === 'processing' && (
              <Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  Please wait while we set up your premium subscription...
                </Typography>
                
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              </Box>
            )}

            {status === 'success' && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom color="success.main" fontWeight="medium">
                  Your subscription is now active!
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Thank you for subscribing to LevelUpTeaching Premium. You now have unlimited access to all our AI-powered teaching tools.
                </Typography>

                <Paper variant="outlined" sx={{ p: 3, mb: 3, textAlign: 'left', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    What's included in your plan:
                  </Typography>
                  {[
                    'Unlimited AI Lesson Plans',
                    'Advanced Student Analytics',
                    'Priority Support',
                    'Export to PDF & Word'
                  ].map((feature, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Paper>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<HomeIcon />}
                    onClick={() => navigate('/dashboard')}
                    sx={{ borderRadius: 2 }}
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<SchoolIcon />}
                    onClick={() => navigate('/subscription')}
                    sx={{ borderRadius: 2 }}
                  >
                    View Subscription
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                  A confirmation email has been sent to your registered email address.
                </Typography>
              </Box>
            )}

            {status === 'error' && (
              <Box sx={{ textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  {errorMessage}
                </Alert>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  If you were charged but see this error, please contact support immediately. Do not attempt to subscribe again.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/subscription')}
                    sx={{ borderRadius: 2 }}
                  >
                    Back to Subscription
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/dashboard')}
                    sx={{ borderRadius: 2 }}
                  >
                    Go to Dashboard
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default SubscriptionSuccess;
